import React, { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { adminApi, uploadImage, imgUrl, errMsg } from "../lib/api";

const SLOTS = [
  { key: "hero", title: "Hero background", hint: "Wide landscape, 1600×1000+. Subject on the right.", aspect: "aspect-[16/10]" },
  { key: "about", title: "About photo", hint: "Portrait, ~3:4. Shown as a tilted polaroid.", aspect: "aspect-[3/4]" },
  { key: "avatar", title: "Chat avatar", hint: "Square-ish face photo for the chat header.", aspect: "aspect-square" },
];

function Slot({ slot, url, refresh }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    try {
      setProgress(0);
      const newUrl = await uploadImage(file, setProgress);
      await adminApi.put(`/admin/site-images/${slot.key}`, { url: newUrl });
      await refresh();
      toast.success(`${slot.title} updated`);
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="admin-card p-5 flex flex-col" data-testid={`site-slot-${slot.key}`}>
      <div className={`relative ${slot.aspect} max-h-[320px] w-full rounded-xl overflow-hidden bg-[#f6eef1]`}>
        {url && <img src={imgUrl(url)} alt={slot.title} className="w-full h-full object-cover" data-testid={`site-slot-img-${slot.key}`} />}
        {progress !== null && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm grid place-items-center p-6">
            <div className="w-full max-w-[200px] text-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#ee3a70] mx-auto" />
              <Progress value={progress} className="mt-3 h-2" />
              <p className="text-[12px] text-[#7a7276] mt-2">Uploading {progress}%</p>
            </div>
          </div>
        )}
      </div>
      <h3 className="text-[16px] font-semibold mt-4 text-[#1b1719]">{slot.title}</h3>
      <p className="text-[13px] text-[#7a7276] mt-1 flex-1">{slot.hint}</p>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`site-slot-input-${slot.key}`} />
      <button onClick={() => ref.current?.click()} disabled={progress !== null} className="admin-btn mt-4 h-10" data-testid={`site-slot-replace-${slot.key}`}>
        <Upload className="w-4 h-4" /> Replace image
      </button>
    </div>
  );
}

export default function SiteImagesPanel({ images, refresh }) {
  return (
    <div className="grid md:grid-cols-3 gap-5 items-start">
      {SLOTS.map((s) => <Slot key={s.key} slot={s} url={images?.[s.key]} refresh={refresh} />)}
    </div>
  );
}
